import { isExplicitDesiredDate } from 'src/stores/_infrastructure/desiredDateFilter';
import { useFilterStore } from 'src/stores/filter';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useTrashListStore } from 'src/stores/trashList';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE_LIST_TYPE } from 'src/models/genericActivityList';
import { PreconfiguredTimeRanges } from 'src/models/miniCalendar';
import { StatusEnum } from 'src/models/status/enums';

import { today } from 'src/util/date';
import { registerListInstanceFetcher, resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { registerModuleTabSettingsReset } from 'src/util/listInstance/moduleTabSettingsReset';
import { QueryStore } from 'src/util/query/QueryStore';

const warmupTrashList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	}
) => {
	const trashListStore = useTrashListStore();

	void trashListStore.fetchFilterBoxes({
		queryStore: queryStore,
	});
	void trashListStore.fetchFilterUsers({
		queryStore: queryStore,
	});

	void fetchTrashList(_store, queryStore, {
		...queryParameters,
		clearResultsBeforeFetch: false,
	});
};

const fetchTrashList = (
	_store: RuntimeStoreContext,
	queryStore: QueryStore,
	queryParameters: {
		activityListType: ActivityListTypeEnum;
		clearResultsBeforeFetch: boolean;
		desiredDate: DateRangeSelection | string;
		hiddenSearchTerm: string;
		searchTerm: string;
	},
	listStore: ReturnType<typeof useTrashListStore> = useTrashListStore()
) => {
	const hasActiveSearch = queryParameters.searchTerm.trim() !== '' || queryParameters.hiddenSearchTerm.trim() !== '';
	const didClearSubscriptionsForSearch = hasActiveSearch;

	if (isExplicitDesiredDate(queryParameters.desiredDate)) {
		listStore.setDateFilterAction({
			dateFilter: queryParameters.desiredDate,
		});
	}

	if (hasActiveSearch) {
		listStore.clearSubscriptions({
			queryStore: queryStore,
		});
	}

	if (queryParameters.clearResultsBeforeFetch) {
		if (
			queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetOpenTrashList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.MINE ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetMineTrashList();
		}

		if (
			queryParameters.activityListType === ActivityListTypeEnum.ALL ||
			queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
		) {
			if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
				listStore.clearSubscriptions({
					queryStore: queryStore,
				});
			}
			listStore.silentSetAllTrashList();
		}
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.OPEN ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.OPEN && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}
		void listStore.fetchOpenTrashList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.MINE ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.MINE && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}
		void listStore.fetchMineTrashList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}

	if (
		queryParameters.activityListType === ActivityListTypeEnum.ALL ||
		queryParameters.activityListType === ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE
	) {
		if (queryParameters.activityListType === ActivityListTypeEnum.ALL && !didClearSubscriptionsForSearch) {
			listStore.clearSubscriptions({
				queryStore: queryStore,
			});
		}
		void listStore.fetchAllTrashList({
			hiddenSearchTerm: queryParameters.hiddenSearchTerm,
			queryStore: queryStore,
			searchTerm: queryParameters.searchTerm,
		});
	}
};

registerListInstanceFetcher('trashList', ({ listStore, params, queryStore, runtimeStore }) =>
	fetchTrashList(runtimeStore as never, queryStore as never, params as never, listStore as never)
);

// Task 2.6 (analog Task 2.3/2.5 / messageList/phoneCallList): Basis-Identifier der drei Sub-Listen-
// Scroll-Positionen (Offen/Meine/Alle) — muss `TrashList.vue`s `virtual-list-id="moduleActivityListScrollerTrash"`
// spiegeln (dort mit Open/Mine/All-Suffix versehen, s. GenericActivityListTabPanel.vue). Kein
// zentraler Export vorhanden, daher hier dieselbe Literal-Konstante wie in TrashList.vue.
const TRASH_LIST_SCROLL_BASE_IDENTIFIER = 'moduleActivityListScrollerTrash';

// Nur die Default-Instanz (Modul-Tabs laufen immer auf ihr) — `?list=`-Instanz-Tabs disposen
// bereits vollständig über useListInstanceLifecycle und laufen nie durch diesen Reset (Task 1.7 im
// Layout-Hook filtert sie über tab.query.list aus). Feldliste 1:1 aus clearAllFilters (FilterComponentActions.ts)
// für 'trashList' abgeleitet, OHNE deren router.replace/refreshTheList: Box-/Benutzer-Filter, Datumsfilter
// (+ Zeitraum-Preset), Richtungsfilter (trashList IST ein isDirectionRoute()-Ziel, analog messageList/mailList/
// phoneCallList, anders als taskList/noteList), gewählte Shared-Query, Such-/Hidden-Suchbegriff, Mehrfachauswahl,
// Scroll-Positionen der drei Sub-Listen, aktive Ansicht.
// NICHT angefasst: sort/listViewModeLarge (persistierte Präferenzen), Datenlisten + Counts + Pagination
// + queryStoreSubscriptions (Menü-Badges/Warmup lesen den Default-Store weiter — kein $reset/$dispose).
const resetTrashListModuleTabSettings = (): void => {
	const trashListStore = useTrashListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();

	trashListStore.setFilteredBoxesAction({ boxIdFilter: [] });
	trashListStore.setFilteredUsersAction({ filterUsers: [] });
	trashListStore.setDateFilterAction({ dateFilter: today() });
	trashListStore.setDateTimeRangeFilter({ dateTimeRangeFilter: PreconfiguredTimeRanges.TODAY });
	trashListStore.setDirectionFilterAction({ directionFilter: 'combined' });
	trashListStore.setSelectedSharedQueryAction({ selectedSharedQuery: null });

	filterStore.setSearchTerm({ module: 'trashList', searchTerm: '' });
	filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'trashList' });

	genericActivityListStore.clearTheSelection({ genericListIdentifier: 'trashList' });
	genericActivityListStore.resetScrollPositions(TRASH_LIST_SCROLL_BASE_IDENTIFIER);
	genericActivityListStore.setActiveView({
		activeView: MODULE_LIST_TYPE.OPEN,
		genericListIdentifier: 'trashList',
	});
};

registerModuleTabSettingsReset('trashList', resetTrashListModuleTabSettings);

const setTheStateForTheSelectedTrashAggregateIds = (
	_store: RuntimeStoreContext,
	_queryStore: QueryStore,
	queryParameters: {
		aggregateIds: string[];
		genericListIdentifier?: string;
		state: StatusEnum;
	}
) => {
	const genericActivityListStore = useGenericActivityListStore();
	const resolved = resolveListInstance(queryParameters.genericListIdentifier);

	// 'missing' (Instanz-Key ohne Registry-Eintrag = Tab bereits geschlossen) → NICHT den Default-Store
	// mutieren; 'instance' → Store DIESER Listen-Instanz; 'default' → Default-Store (byte-identisch zu vorher).
	if (resolved.outcome !== 'missing') {
		const trashListStore: ReturnType<typeof useTrashListStore> =
			resolved.outcome === 'instance' ? (resolved.listStore as never) : useTrashListStore();

		switch (queryParameters.state) {
			case StatusEnum.UNSEEN:
				trashListStore.setTheTrashStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.SEEN:
				trashListStore.setTheTrashStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			case StatusEnum.READY:
				trashListStore.setTheTrashStateToReady({
					aggregateIds: queryParameters.aggregateIds,
				});
				break;
			default:
				break;
		}
	}

	void genericActivityListStore.clearTheSelection({
		genericListIdentifier: queryParameters['genericListIdentifier'] ?? 'trashList',
	});
};

export { fetchTrashList, setTheStateForTheSelectedTrashAggregateIds, warmupTrashList };
